/**
 * The dashboard object model.
 *
 * Two things are deliberately kept apart here. *Placement* — where a tile sits
 * on the grid — knows nothing about what a widget is, so layout code never
 * grows a switch on widget kind. *Configuration* is a discriminated union, one
 * arm per widget kind, so a lap list cannot be handed a track-status setting
 * and adding a kind is a compile error everywhere it is not handled.
 *
 * The registry of views lives in the app, because it holds components. Every
 * non-visual fact about a widget — its label, its smallest sensible size, its
 * defaults, how to read an old document, and what data it needs — lives here,
 * so the model can be reasoned about and tested without rendering anything.
 */

import type { Timestamp } from 'firebase/firestore'

// ------------------------------------------------------------------ cars

/**
 * Which car a widget is about.
 *
 * The car is a parameter of *viewing* a dashboard, not a property of it: a
 * layout with 436 baked into eight widgets dies the moment the number changes
 * and cannot be reused for the team's other car. So the ordinary case is
 * `primary`, meaning "whichever car this dashboard is being viewed for".
 *
 * Pinning is the escape hatch for watching a rival, and is named as such so it
 * reads as the exception at every call site.
 */
export type CarRef = { kind: 'primary' } | { kind: 'pinned'; number: string }

/** The car a widget should show, or null when there is none to show. */
export function resolveCar(ref: CarRef | undefined, primary: string | null): string | null {
  if (!ref || ref.kind === 'primary') return primary
  return ref.number.trim() || null
}

// --------------------------------------------------------------- widgets

/** Columns a lap list can show. */
export type LapColumn = 'lap' | 'time' | 's1' | 's2' | 's3' | 'position' | 'gap'

export const lapColumns: readonly LapColumn[] = [
  'lap',
  'time',
  's1',
  's2',
  's3',
  'position',
  'gap',
]

/**
 * Every widget's configuration, one arm per kind.
 *
 * A widget with nothing to configure still gets an arm, so that giving it a
 * setting later is a change in one place rather than a change of shape.
 */
export type WidgetConfig =
  | { kind: 'carNumber' }
  | { kind: 'position'; of: 'overall' | 'class' }
  | { kind: 'lapNumber' }
  | { kind: 'lapTime'; which: 'last' | 'best' }
  | { kind: 'sectors' }
  | { kind: 'lapList'; rows: number; columns: LapColumn[] }
  | { kind: 'messages'; rows: number }
  | { kind: 'trackStatus' }
  | { kind: 'clock'; show: 'remaining' | 'elapsed' | 'timeOfDay' }
  | { kind: 'stopBy' }
  | { kind: 'pitWindow' }
  | { kind: 'driver' }
  | { kind: 'stint' }
  | { kind: 'fuel'; rows: number }
  | { kind: 'fuelStrategy' }
  | { kind: 'driverBudget' }
  | { kind: 'sinceLastLap' }
  | { kind: 'gaps' }

export type WidgetKind = WidgetConfig['kind']
export type ConfigFor<K extends WidgetKind> = Extract<WidgetConfig, { kind: K }>

/**
 * What a widget reads. Declared rather than discovered, so a dashboard opens
 * one listener per distinct need and fans out, instead of one per widget.
 *
 * Twelve tiles on a pit-wall connection is the difference between one
 * reconnection to manage and twelve.
 */
export type DataNeed =
  | 'session'
  | 'standings'
  /** The watched car's laps — a window of recent ones. */
  | 'laps'
  /**
   * The watched car's laps, all of them.
   *
   * Its own need rather than a bigger window for everybody: a tile showing the
   * last ten laps has no use for six hundred documents, and on a pit-wall
   * connection the difference is the page loading or not.
   */
  | 'allLaps'
  /** The race-control log, oldest kept. */
  | 'messages'
  /** What race control is showing now — a different question from the log. */
  | 'liveMessages'
  | 'stints'
  /**
   * Completed pit stops for the whole field.
   *
   * Its own need rather than part of `stints`: the stint chart wants the
   * materialised aggregates, while driving time and the stop-by deadline want
   * the individual stops, and most screens want neither.
   */
  | 'pitStops'
  | 'fcy'
  /**
   * The watched car's session bests — best lap, best of each sector, ideal.
   *
   * One document, not the collection the stats page listens to. A tile wanting
   * this car's best third sector should not be reading ninety cars' worth of
   * minima to find it.
   */
  | 'carSummary'
  /** Refuelling for the whole field, matched to stops by the fuel job. */
  | 'fuel'

// -------------------------------------------------------------- placement

/** One widget on the grid. */
export interface WidgetPlacement {
  /** Stable for the widget's life, so moving it is not deleting and recreating. */
  id: string
  x: number
  y: number
  w: number
  h: number
  /** Absent means the dashboard's primary car, which is the ordinary case. */
  car?: CarRef
  config: WidgetConfig
}

/**
 * A stored widget whose kind this build does not know about.
 *
 * Kept rather than dropped. A dashboard opened by an older client, or after a
 * widget is retired, must show one dead tile — not lose the tile silently, and
 * certainly not fail to render during a race.
 */
export interface UnknownWidget {
  id: string
  x: number
  y: number
  w: number
  h: number
  unknownKind: string
}

export type LayoutEntry = WidgetPlacement | UnknownWidget

export function isKnownWidget(e: LayoutEntry): e is WidgetPlacement {
  return 'config' in e
}

/**
 * A dashboard's layout.
 *
 * The grid is in cells, not pixels, and the viewer makes every cell an equal
 * fraction of the viewport — so a dashboard fills whatever screen it is on and
 * never scrolls. `rows` therefore has to be stored: it is what cell height, and
 * with it the whole type scale, is derived from.
 */
export interface DashboardLayout {
  columns: number
  rows: number
  widgets: LayoutEntry[]
}

export const defaultColumns = 12
export const defaultRows = 8

// -------------------------------------------------------------- metadata

export interface WidgetMeta<K extends WidgetKind> {
  label: string
  /** One line, shown when picking a widget. */
  description: string
  /**
   * The smallest size at which the widget is still readable.
   *
   * This is what replaces breakpoints. A dashboard scales rather than reflows,
   * so nothing ever gets narrower than it was designed for — the only way to
   * make a widget unreadable is to place it too small, and the editor refuses.
   */
  min: { w: number; h: number }
  default: ConfigFor<K>
  /**
   * Read a stored config, filling in anything missing.
   *
   * This is the whole migration story: defaults on read, no version numbers. A
   * dashboard saved today opens unchanged after a field is added tomorrow.
   */
  parse: (raw: Record<string, unknown>) => ConfigFor<K>
  needs: readonly DataNeed[]
}

/**
 * Every widget's starting configuration, in one place.
 *
 * Separate from widgetMeta below so that `default` and the fallbacks inside
 * `parse` are the same values rather than two copies that agree today. They
 * have to: a widget added to a dashboard and a widget read back from one with
 * nothing stored are the same widget, and if the two disagree then "the
 * default" quietly means two different things depending on how you got there.
 */
// Never handed out by reference: `parse` returns a fresh object and callers
// creating a widget copy it, so a config edited on one dashboard cannot reach
// through and change what every other dashboard starts from.
const widgetDefaults: { [K in WidgetKind]: ConfigFor<K> } = {
  carNumber: { kind: 'carNumber' },
  position: { kind: 'position', of: 'overall' },
  lapNumber: { kind: 'lapNumber' },
  lapTime: { kind: 'lapTime', which: 'last' },
  sectors: { kind: 'sectors' },
  lapList: { kind: 'lapList', rows: 10, columns: ['lap', 'time', 's1', 's2', 's3'] },
  messages: { kind: 'messages', rows: 6 },
  trackStatus: { kind: 'trackStatus' },
  clock: { kind: 'clock', show: 'remaining' },
  stopBy: { kind: 'stopBy' },
  pitWindow: { kind: 'pitWindow' },
  driver: { kind: 'driver' },
  stint: { kind: 'stint' },
  fuel: { kind: 'fuel', rows: 4 },
  fuelStrategy: { kind: 'fuelStrategy' },
  driverBudget: { kind: 'driverBudget' },
  sinceLastLap: { kind: 'sinceLastLap' },
  gaps: { kind: 'gaps' },
}

/**
 * The mapped type is load-bearing: adding an arm to WidgetConfig without
 * adding an entry here does not compile.
 */
export const widgetMeta: { [K in WidgetKind]: WidgetMeta<K> } = {
  stopBy: {
    label: 'Stop by',
    description:
      'When this car must next stop for a stint limit or a driver running out of time, and how many laps that leaves. Needs a race plan. Pit windows have their own tile.',
    min: { w: 2, h: 2 },
    default: widgetDefaults.stopBy,
    parse: () => ({ ...widgetDefaults.stopBy }),
    needs: ['session', 'standings', 'laps', 'pitStops', 'stints', 'fcy'],
  },
  pitWindow: {
    label: 'Pit window',
    description:
      'Open or closed, how long until it changes, and how many more laps this car can do before it misses it. Needs a race plan with fixed windows.',
    min: { w: 2, h: 2 },
    default: widgetDefaults.pitWindow,
    parse: () => ({ ...widgetDefaults.pitWindow }),
    needs: ['session', 'standings', 'laps', 'pitStops', 'fcy', 'carSummary'],
  },
  driver: {
    label: 'Driver',
    description:
      'Who is in the car and who the plan says is next, with a warning when the two have parted company.',
    min: { w: 2, h: 1 },
    default: widgetDefaults.driver,
    parse: () => ({ ...widgetDefaults.driver }),
    needs: ['session', 'laps', 'pitStops', 'stints'],
  },
  fuel: {
    label: 'Fuel',
    description:
      'The last few fills for this car: when, how much, at which stop, and how many laps the fuel before it lasted. Needs the event to have a fuel source.',
    min: { w: 2, h: 2 },
    default: widgetDefaults.fuel,
    parse: (r) => ({
      kind: 'fuel',
      rows: intIn(r.rows, 1, 12, widgetDefaults.fuel.rows),
    }),
    needs: ['fuel'],
  },
  fuelStrategy: {
    label: 'Fuel strategy',
    description:
      'Laps of fuel in the car, whether that reaches the next window and the one after, and so whether this window has to take fuel. Needs a race plan with fixed windows and a tank size, and the event to have a fuel source.',
    min: { w: 2, h: 2 },
    default: widgetDefaults.fuelStrategy,
    parse: () => ({ ...widgetDefaults.fuelStrategy }),
    // Standings and stops for whether the car has already stopped in this window.
    needs: ['session', 'standings', 'laps', 'pitStops'],
  },
  stint: {
    label: 'Stint',
    description:
      "Who is in the car, how long they have been in it and how many laps. A stop without a driver change — fuel, a repair — does not reset it. Against the plan's maximum stint where it sets one.",
    min: { w: 2, h: 2 },
    default: widgetDefaults.stint,
    parse: () => ({ ...widgetDefaults.stint }),
    // Standings for whether the car is in the pits now, which holds the clock.
    needs: ['session', 'standings', 'laps', 'pitStops', 'stints'],
  },
  sinceLastLap: {
    label: 'Since last lap',
    description:
      'How long since this car crossed the line. Boring until it is the only thing that matters — trouble shows up as a car that is late long before anybody has been told.',
    min: { w: 2, h: 1 },
    default: widgetDefaults.sinceLastLap,
    parse: () => ({ ...widgetDefaults.sinceLastLap }),
    // Standings for the car's own state, which is what separates "late and
    // nobody knows why" from "in the pits" — see SinceLastLap.
    needs: ['session', 'laps', 'standings'],
  },
  gaps: {
    label: 'Gaps',
    description:
      'The cars either side and the gaps to them, with whether you are catching or being caught.',
    // Two rows of two lines. Anything shorter loses the trend, which is the
    // half of this that is hard to work out by watching.
    min: { w: 2, h: 2 },
    default: widgetDefaults.gaps,
    parse: () => ({ ...widgetDefaults.gaps }),
    // Session as well as standings, for whether capture is still live: a trend
    // measured across the end of a session is measuring nothing.
    needs: ['session', 'standings'],
  },
  driverBudget: {
    label: 'Driver budget',
    description:
      'How much driving time and how many stints each driver has left. Needs a race plan.',
    min: { w: 3, h: 2 },
    default: widgetDefaults.driverBudget,
    parse: () => ({ ...widgetDefaults.driverBudget }),
    needs: ['session', 'laps', 'pitStops', 'stints'],
  },
  carNumber: {
    label: 'Car number',
    description: 'The number of the car being watched.',
    min: { w: 1, h: 1 },
    default: widgetDefaults.carNumber,
    parse: () => ({ ...widgetDefaults.carNumber }),
    needs: ['standings'],
  },
  position: {
    label: 'Position',
    description: 'Overall position, or position in class.',
    min: { w: 1, h: 1 },
    default: widgetDefaults.position,
    parse: (r) => ({
      kind: 'position',
      of: oneOf(r.of, ['overall', 'class'] as const, widgetDefaults.position.of),
    }),
    needs: ['standings'],
  },
  lapNumber: {
    label: 'Current lap',
    description: 'The lap the car is on.',
    min: { w: 1, h: 1 },
    default: widgetDefaults.lapNumber,
    parse: () => ({ ...widgetDefaults.lapNumber }),
    needs: ['standings'],
  },
  lapTime: {
    label: 'Lap time',
    description: 'The last lap, or the best of the session so far.',
    min: { w: 2, h: 1 },
    default: widgetDefaults.lapTime,
    parse: (r) => ({
      kind: 'lapTime',
      which: oneOf(r.which, ['last', 'best'] as const, widgetDefaults.lapTime.which),
    }),
    needs: ['standings'],
  },
  sectors: {
    label: 'Current sectors',
    description:
      "Sector times for the lap in progress, with this car's session best under each.",
    // A row taller than it was: the bests sit under the running times, and at
    // one row high they had nowhere to go.
    min: { w: 3, h: 2 },
    default: widgetDefaults.sectors,
    parse: () => ({ ...widgetDefaults.sectors }),
    needs: ['laps', 'carSummary'],
  },
  lapList: {
    label: 'Recent laps',
    description: 'The last few laps, most recent first.',
    min: { w: 3, h: 2 },
    default: widgetDefaults.lapList,
    parse: (r) => ({
      kind: 'lapList',
      rows: intIn(r.rows, 1, 60, widgetDefaults.lapList.rows),
      columns: subsetOf(r.columns, lapColumns, widgetDefaults.lapList.columns),
    }),
    needs: ['laps'],
  },
  messages: {
    label: 'Race control',
    description: 'Messages from the race director, newest first.',
    min: { w: 3, h: 2 },
    default: widgetDefaults.messages,
    parse: (r) => ({ kind: 'messages', rows: intIn(r.rows, 1, 40, widgetDefaults.messages.rows) }),
    needs: ['messages'],
  },
  trackStatus: {
    label: 'Track status',
    description: 'Green, yellow, safety car, red.',
    min: { w: 2, h: 1 },
    default: widgetDefaults.trackStatus,
    parse: () => ({ ...widgetDefaults.trackStatus }),
    needs: ['session'],
  },
  clock: {
    label: 'Clock',
    description: 'Time remaining, time elapsed, or the time of day.',
    min: { w: 2, h: 1 },
    default: widgetDefaults.clock,
    parse: (r) => ({
      kind: 'clock',
      show: oneOf(r.show, ['remaining', 'elapsed', 'timeOfDay'] as const, widgetDefaults.clock.show),
    }),
    // Time of day included, deliberately. It is read from the clock the capture
    // job stamps rather than the viewer's own, because a laptop on a pit wall
    // is exactly the machine whose clock is quietly wrong. Unlike the other
    // two it keeps running after the session ends — see Clock.tsx.
    needs: ['session'],
  },
}

export const widgetKinds = Object.keys(widgetMeta) as WidgetKind[]

function isWidgetKind(v: unknown): v is WidgetKind {
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(widgetMeta, v)
}

/** Every distinct listener a set of widgets requires, and no more. */
export function neededData(entries: readonly LayoutEntry[]): DataNeed[] {
  const needs = new Set<DataNeed>()
  for (const e of entries) {
    if (!isKnownWidget(e)) continue
    for (const n of widgetMeta[e.config.kind].needs) needs.add(n)
  }
  return [...needs]
}

// ----------------------------------------------------------------- parse

/**
 * Read a stored layout.
 *
 * Total: any input produces a renderable layout. A dashboard is looked at
 * during a race, so a malformed document has to degrade to an empty grid or a
 * dead tile rather than to a stack trace.
 */
export function parseLayout(raw: unknown): DashboardLayout {
  const obj = asRecord(raw)
  const widgets = Array.isArray(obj.widgets) ? obj.widgets : []
  const columns = intIn(obj.columns, 1, 48, defaultColumns)
  const rows = intIn(obj.rows, 1, 24, defaultRows)

  const out: LayoutEntry[] = []
  for (const [i, w] of widgets.entries()) {
    out.push(parseEntry(w, i, columns, rows))
  }
  return { columns, rows, widgets: out }
}

function parseEntry(raw: unknown, index: number, columns: number, rows: number): LayoutEntry {
  const r = asRecord(raw)
  const id = typeof r.id === 'string' && r.id ? r.id : `w${index}`
  const box = {
    x: intIn(r.x, 0, columns - 1, 0),
    y: intIn(r.y, 0, rows - 1, 0),
    w: intIn(r.w, 1, columns, 1),
    h: intIn(r.h, 1, rows, 1),
  }

  const config = asRecord(r.config)
  if (!isWidgetKind(config.kind)) {
    return { id, ...box, unknownKind: typeof config.kind === 'string' ? config.kind : 'unknown' }
  }

  // The cast is the one place the mapped type has to be taken on trust: the
  // guard above has established the kind, but TypeScript cannot carry that
  // through an index into a map of per-kind parsers.
  const meta = widgetMeta[config.kind] as WidgetMeta<WidgetKind>
  return { id, ...box, car: parseCarRef(r.car), config: meta.parse(config) }
}

function parseCarRef(raw: unknown): CarRef | undefined {
  const r = asRecord(raw)
  if (r.kind === 'pinned' && typeof r.number === 'string' && r.number.trim()) {
    return { kind: 'pinned', number: r.number.trim() }
  }
  // Anything else, including absent, means the dashboard's own car.
  return undefined
}

// --------------------------------------------------------------- helpers

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}
}

function oneOf<T extends string>(v: unknown, allowed: readonly T[], fallback: T): T {
  return typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : fallback
}

function intIn(v: unknown, min: number, max: number, fallback: number): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) return fallback
  return Math.min(max, Math.max(min, Math.round(v)))
}

function subsetOf<T extends string>(v: unknown, allowed: readonly T[], fallback: T[]): T[] {
  if (!Array.isArray(v)) return [...fallback]
  const kept = v.filter(
    (x): x is T => typeof x === 'string' && (allowed as readonly string[]).includes(x),
  )
  // Order is the author's, but duplicates are not meaningful.
  const seen = new Set<T>()
  const out = kept.filter((x) => {
    if (seen.has(x)) return false
    seen.add(x)
    return true
  })
  return out.length > 0 ? out : [...fallback]
}

// ------------------------------------------------------------- documents

/** The layout as stored on a dashboard document. */
export interface DashboardLayoutDoc {
  columns: number
  rows: number
  widgets: unknown[]
  updatedAt?: Timestamp
}
